/// IALA Maritime Buoyage System.
///
/// One global standard split into two regions:
///   - Region A (Europe, Africa, Asia, Australasia): port-hand
///     buoys are RED, starboard-hand are GREEN.
///   - Region B (Americas, Japan, Korea, Philippines): port-hand
///     are GREEN, starboard-hand are RED.
///
/// Lateral marks flip between regions. Cardinal, isolated-danger,
/// safe-water, special, and emergency-wreck marks are identical
/// worldwide. The `Region` flag below toggles the renderer.

export type Region = "A" | "B";

export const REGIONS: readonly Region[] = ["A", "B"];

export function regionLabel(r: Region): string {
  return r === "A"
    ? "Region A — UK / Europe / most of world"
    : "Region B — Americas / Japan / Korea";
}

/// Buoy body styles. Drives the SVG renderer in
/// `apps/web/src/ui/BuoyFigure.tsx`.
///   - `can`     — flat-topped cylinder (port-hand in Region A)
///   - `cone`    — conical shape, apex up (starboard-hand in
///                 Region A)
///   - `spar`    — vertical pole, no body — cardinals + safe water
///                 in Europe often rendered this way
///   - `pillar`  — tall rectangular body (specials, cardinals,
///                 isolated-danger marks)
///   - `sphere`  — spherical body (safe water)
export type BodyShape = "can" | "cone" | "spar" | "pillar" | "sphere";

/// Topmark — the shape on top of the buoy. Per IALA, lateral marks
/// proper topmarks ARE the silhouette shape repeated (single can on
/// a port-hand mark, single cone-up on a starboard-hand mark) —
/// distinct from the cardinal cone-pair topmarks. Lateral topmarks
/// are technically optional on pillar variants but Trinity House
/// shows them by default.
///
///   - `can`       — flat-topped cylinder (port-hand, preferred-
///                   channel-to-starboard)
///   - `cone-up`   — solid cone, apex up (starboard-hand, preferred-
///                   channel-to-port)
///   - `cone-down` — solid cone, apex down (unused in current IALA;
///                   retained for back-compat)
///   - `2-cones-points-up` — two cones, both apex up (N cardinal)
///   - `2-cones-points-down` — both apex down (S cardinal)
///   - `2-cones-base-to-base` — apexes outward, bases meet (E)
///   - `2-cones-points-together` — apexes inward (W)
///   - `2-spheres` — two spheres vertically (isolated danger)
///   - `sphere`    — single sphere (safe water)
///   - `x`         — yellow X / saltire (special)
///   - `cross`     — yellow upright cross (emergency-wreck)
export type Topmark =
  | "can"
  | "cone-up"
  | "cone-down"
  | "2-cones-points-up"
  | "2-cones-points-down"
  | "2-cones-base-to-base"
  | "2-cones-points-together"
  | "2-spheres"
  | "sphere"
  | "x"
  | "cross"
  | "none";

/// Body colour scheme. Stripes are vertical/horizontal blocks listed
/// top-to-bottom (or left-to-right for vertical stripes). "blue" is
/// reserved for the IALA Emergency Wreck-Marking buoy (Berlin-blue
/// alternating with yellow stripes); it is not used elsewhere.
export type BodyColour =
  | "red"
  | "green"
  | "yellow"
  | "black"
  | "white"
  | "blue";

export interface ColourPattern {
  /// Either "horizontal" stripes (cardinals, isolated danger) or
  /// "vertical" stripes (safe water, emergency wreck), or "solid"
  /// (lateral marks, specials).
  layout: "solid" | "horizontal" | "vertical";
  bands: readonly BodyColour[];
}

/// A characteristic spelled out the way the Admiralty Light List
/// would print it ("Fl(2)10s", "Q", "Q+LFl15s"). Plain text — the
/// game just shows it on the reveal panel.
export interface LightCharacteristic {
  /// Full IALA-style description ("Group flashing two, every 10
  /// seconds"). Used as the explainer.
  description: string;
  /// Compact almanac-style abbreviation.
  abbreviation: string;
  /// Colour of the flash. Yellow on specials, white elsewhere
  /// unless the lateral mark specifies otherwise (red = port-hand
  /// flash in Region A, green = starboard).
  colour: BodyColour;
}

export type MarkCategory =
  | "lateral"
  | "cardinal"
  | "isolated-danger"
  | "safe-water"
  | "special"
  | "emergency-wreck";

export interface IalaMark {
  /// Stable identifier — used as the flashcards answer key.
  id: string;
  /// Display name surfaced on cards and the reveal.
  name: string;
  category: MarkCategory;
  /// Long-form description of what the mark means.
  meaning: string;
  /// Short label for multiple-choice buttons.
  shortMeaning: string;
  /// Body colour pattern — Region B may invert this for lateral
  /// marks.
  body: ColourPattern;
  /// Body silhouette.
  bodyShape: BodyShape;
  /// Top of the buoy.
  topmark: Topmark;
  /// Colour of the topmark — almost always black except for the
  /// yellow X on specials.
  topmarkColour: BodyColour;
  /// Whether this mark inverts between regions (lateral) or stays
  /// the same (everything else).
  regional: boolean;
  light: LightCharacteristic;
}

/// Cardinal-mark mnemonic. Each cardinal sector points away from
/// the danger — a North mark is north of the hazard, ships should
/// pass NORTH of the mark. Topmark orientation maps to a clock:
///   - N (12 o'clock): both cones point UP
///   - E (3 o'clock):  cones point OUTWARD (base-to-base)
///   - S (6 o'clock):  both cones point DOWN
///   - W (9 o'clock):  cones point INWARD (point-to-point)
/// Colour bands follow the same mnemonic — yellow where the cones
/// point ("yellow at the points"). The light is white quick or
/// very-quick, with letter-of-the-compass groupings:
///   - N: continuous Q or VQ
///   - E: VQ(3) every 5 s or Q(3) every 10 s
///   - S: VQ(6) + LFl every 10 s or Q(6) + LFl every 15 s
///   - W: VQ(9) every 10 s or Q(9) every 15 s
export const IALA_MARKS: Readonly<Record<string, IalaMark>> = Object.freeze({
  port_hand: {
    id: "port_hand",
    name: "Port-hand lateral",
    category: "lateral",
    meaning:
      "Mark the port side of the channel when entering from seaward — leave to your port (left) on the way in.",
    shortMeaning: "Leave to port on entry",
    body: { layout: "solid", bands: ["red"] },
    bodyShape: "can",
    /// Per Trinity House: "Single red cylinder (can)". The topmark
    /// echoes the silhouette of the body — not a cone-down (which is
    /// the long-obsolete pre-IALA marker some chart libraries still
    /// reach for by mistake).
    topmark: "can",
    topmarkColour: "red",
    regional: true,
    light: {
      description: "Any rhythm — usually flashing red.",
      abbreviation: "Fl.R",
      colour: "red",
    },
  },
  starboard_hand: {
    id: "starboard_hand",
    name: "Starboard-hand lateral",
    category: "lateral",
    meaning:
      "Mark the starboard side of the channel when entering from seaward — leave to your starboard (right) on the way in.",
    shortMeaning: "Leave to starboard on entry",
    body: { layout: "solid", bands: ["green"] },
    bodyShape: "cone",
    topmark: "cone-up",
    topmarkColour: "green",
    regional: true,
    light: {
      description: "Any rhythm — usually flashing green.",
      abbreviation: "Fl.G",
      colour: "green",
    },
  },
  preferred_port: {
    id: "preferred_port",
    name: "Preferred channel to starboard",
    category: "lateral",
    meaning:
      "Channel divides — the preferred (deeper / main) channel lies to starboard. Leave this mark to port.",
    shortMeaning: "Preferred channel to stbd",
    body: { layout: "horizontal", bands: ["red", "green", "red"] },
    bodyShape: "can",
    /// Same can-topmark rule as a plain port-hand mark — this is
    /// still a port-hand mark in disguise, just with an inserted
    /// green band signalling that the preferred channel diverges.
    topmark: "can",
    topmarkColour: "red",
    regional: true,
    light: {
      description: "Composite group flashing (2+1) red.",
      abbreviation: "Fl(2+1).R",
      colour: "red",
    },
  },
  preferred_starboard: {
    id: "preferred_starboard",
    name: "Preferred channel to port",
    category: "lateral",
    meaning:
      "Channel divides — the preferred channel lies to port. Leave this mark to starboard.",
    shortMeaning: "Preferred channel to port",
    body: { layout: "horizontal", bands: ["green", "red", "green"] },
    bodyShape: "cone",
    topmark: "cone-up",
    topmarkColour: "green",
    regional: true,
    light: {
      description: "Composite group flashing (2+1) green.",
      abbreviation: "Fl(2+1).G",
      colour: "green",
    },
  },
  north_cardinal: {
    id: "north_cardinal",
    name: "North cardinal",
    category: "cardinal",
    meaning:
      "Pass to the north of this mark. Cones both point up; yellow band on top, black below.",
    shortMeaning: "Pass to the north",
    body: { layout: "horizontal", bands: ["black", "yellow"] },
    bodyShape: "pillar",
    topmark: "2-cones-points-up",
    topmarkColour: "black",
    regional: false,
    light: {
      description: "Continuous quick or very-quick white flash.",
      abbreviation: "VQ or Q",
      colour: "white",
    },
  },
  east_cardinal: {
    id: "east_cardinal",
    name: "East cardinal",
    category: "cardinal",
    meaning:
      "Pass to the east of this mark. Cones base-to-base (apexes point outward); black-yellow-black bands.",
    shortMeaning: "Pass to the east",
    body: { layout: "horizontal", bands: ["black", "yellow", "black"] },
    bodyShape: "pillar",
    topmark: "2-cones-base-to-base",
    topmarkColour: "black",
    regional: false,
    light: {
      description:
        "Three quick or very-quick white flashes every 5 or 10 seconds.",
      abbreviation: "VQ(3) 5s or Q(3) 10s",
      colour: "white",
    },
  },
  south_cardinal: {
    id: "south_cardinal",
    name: "South cardinal",
    category: "cardinal",
    meaning:
      "Pass to the south of this mark. Cones both point down; yellow band on top, black below.",
    shortMeaning: "Pass to the south",
    body: { layout: "horizontal", bands: ["yellow", "black"] },
    bodyShape: "pillar",
    topmark: "2-cones-points-down",
    topmarkColour: "black",
    regional: false,
    light: {
      description:
        "Six quick or very-quick flashes plus a long flash, every 10 or 15 seconds.",
      abbreviation: "VQ(6)+LFl 10s or Q(6)+LFl 15s",
      colour: "white",
    },
  },
  west_cardinal: {
    id: "west_cardinal",
    name: "West cardinal",
    category: "cardinal",
    meaning:
      "Pass to the west of this mark. Cones point-to-point (apexes meet); yellow-black-yellow bands.",
    shortMeaning: "Pass to the west",
    body: { layout: "horizontal", bands: ["yellow", "black", "yellow"] },
    bodyShape: "pillar",
    topmark: "2-cones-points-together",
    topmarkColour: "black",
    regional: false,
    light: {
      description:
        "Nine quick or very-quick white flashes every 10 or 15 seconds.",
      abbreviation: "VQ(9) 10s or Q(9) 15s",
      colour: "white",
    },
  },
  isolated_danger: {
    id: "isolated_danger",
    name: "Isolated danger",
    category: "isolated-danger",
    meaning:
      "Stationed on a danger (wreck, rock) with navigable water all around. Black body with red horizontal bands, two-sphere topmark.",
    shortMeaning: "Isolated danger — pass clear either side",
    body: { layout: "horizontal", bands: ["black", "red", "black"] },
    bodyShape: "pillar",
    topmark: "2-spheres",
    topmarkColour: "black",
    regional: false,
    light: {
      description: "Group flashing two white every 5 seconds.",
      abbreviation: "Fl(2) 5s",
      colour: "white",
    },
  },
  safe_water: {
    id: "safe_water",
    name: "Safe water",
    category: "safe-water",
    meaning:
      "Navigable water all around — typically a fairway centreline or landfall mark. Red and white vertical stripes, single red sphere topmark.",
    shortMeaning: "Safe water — fairway or landfall",
    body: { layout: "vertical", bands: ["red", "white"] },
    bodyShape: "sphere",
    topmark: "sphere",
    topmarkColour: "red",
    regional: false,
    light: {
      description: "Isophase, occulting, single long flash 10 s, or Morse A.",
      abbreviation: "Iso / Oc / LFl 10s / Mo(A)",
      colour: "white",
    },
  },
  special: {
    id: "special",
    name: "Special mark",
    category: "special",
    meaning:
      "Marks a special feature — cable, pipeline, military exercise area, traffic separation, recreation zone. Yellow body, yellow X topmark.",
    shortMeaning: "Special feature — see chart",
    body: { layout: "solid", bands: ["yellow"] },
    bodyShape: "pillar",
    topmark: "x",
    topmarkColour: "yellow",
    regional: false,
    light: {
      description: "Any rhythm yellow — not a white-light rhythm.",
      abbreviation: "Fl.Y",
      colour: "yellow",
    },
  },
  emergency_wreck: {
    id: "emergency_wreck",
    name: "Emergency wreck",
    category: "emergency-wreck",
    meaning:
      "Marks a new wreck before chart updates can be issued. Blue and yellow vertical stripes, yellow upright cross topmark.",
    shortMeaning: "New wreck — give wide berth",
    /// Per Trinity House: "Blue/Yellow vertical stripes in equal
    /// number" (min 4, max 8). The renderer draws 4 stripes — yellow
    /// + blue + yellow + blue, left-to-right — which matches the
    /// standard photograph on every Trinity House data sheet.
    body: { layout: "vertical", bands: ["yellow", "blue", "yellow", "blue"] },
    bodyShape: "pillar",
    topmark: "cross",
    topmarkColour: "yellow",
    regional: false,
    light: {
      description: "Alternating blue and yellow, 1 s on / 0.5 s off.",
      abbreviation: "Al.BuY 3s",
      colour: "yellow",
    },
  },
});

export const IALA_IDS: readonly string[] = Object.keys(IALA_MARKS);

/// Lateral-mark inversion for Region B. Anything else passes
/// through unchanged. The renderer should call this when picking
/// which colour to draw the body.
export function bodyForRegion(
  mark: IalaMark,
  region: Region,
): ColourPattern {
  if (!mark.regional || region === "A") return mark.body;
  const swapped = mark.body.bands.map((c): BodyColour => {
    if (c === "red") return "green";
    if (c === "green") return "red";
    return c;
  });
  return { layout: mark.body.layout, bands: swapped };
}

/// Body-shape inversion for Region B lateral marks — Region B port
/// is a green can, starboard is a red cone (shapes don't change).
/// We keep the helper symmetric so callers don't have to remember
/// which fields invert and which don't.
export function bodyShapeForRegion(
  mark: IalaMark,
  _region: Region,
): BodyShape {
  return mark.bodyShape;
}

/// Topmark colour inversion for Region B lateral marks. Topmark
/// shape doesn't change between regions — only colour follows the
/// body.
export function topmarkColourForRegion(
  mark: IalaMark,
  region: Region,
): BodyColour {
  if (!mark.regional || region === "A") return mark.topmarkColour;
  if (mark.topmarkColour === "red") return "green";
  if (mark.topmarkColour === "green") return "red";
  return mark.topmarkColour;
}

/// Light-flash colour for Region B lateral marks.
export function lightColourForRegion(
  mark: IalaMark,
  region: Region,
): BodyColour {
  if (!mark.regional || region === "A") return mark.light.colour;
  if (mark.light.colour === "red") return "green";
  if (mark.light.colour === "green") return "red";
  return mark.light.colour;
}

/// Region-aware light characteristic. The RHYTHM (Fl, Fl(2+1), Q,
/// Mo(A), etc.) is identical worldwide — the IALA standard never
/// varies rhythm by region. Only the COLOUR inverts on lateral
/// marks: Region A red ↔ Region B green and vice versa.
///
/// This helper swaps:
///   - `colour`         via lightColourForRegion
///   - `abbreviation`   .R ↔ .G (Fl.R → Fl.G, Fl(2+1).R → Fl(2+1).G)
///   - `description`    occurrences of "red" ↔ "green"
///
/// Non-regional marks (cardinals, safe water, isolated danger,
/// special, emergency wreck) pass through unchanged because they
/// look identical in both regions.
export function lightForRegion(
  mark: IalaMark,
  region: Region,
): LightCharacteristic {
  if (!mark.regional || region === "A") return mark.light;
  return {
    description: swapRedGreen(mark.light.description),
    abbreviation: swapAbbrevColour(mark.light.abbreviation),
    colour: lightColourForRegion(mark, region),
  };
}

/// Swap `red`↔`green` (case-insensitive, preserving case) in a
/// human-readable description. Uses a sentinel during the first
/// pass so we don't end up with double-swapped text.
function swapRedGreen(s: string): string {
  // Two distinct placeholders that don't contain "red" or "green"
  // as substrings — the previous single "REDGREEN" sentinel hit a
  // recursive /RED/ match on its own prefix. Walk red→placeholder1
  // and green→placeholder2 first, then swap each placeholder to the
  // OPPOSITE word + case in the final pass.
  const RED_P = "@@RP@@";
  const GREEN_P = "@@GP@@";
  return s
    .replace(/red/g, RED_P + "l")
    .replace(/Red/g, RED_P + "c")
    .replace(/RED/g, RED_P + "u")
    .replace(/green/g, GREEN_P + "l")
    .replace(/Green/g, GREEN_P + "c")
    .replace(/GREEN/g, GREEN_P + "u")
    .replace(new RegExp(RED_P + "u", "g"), "GREEN")
    .replace(new RegExp(RED_P + "c", "g"), "Green")
    .replace(new RegExp(RED_P + "l", "g"), "green")
    .replace(new RegExp(GREEN_P + "u", "g"), "RED")
    .replace(new RegExp(GREEN_P + "c", "g"), "Red")
    .replace(new RegExp(GREEN_P + "l", "g"), "red");
}

/// Swap `.R` ↔ `.G` on the colour suffix of a light abbreviation.
/// Only acts on the final colour token so we don't accidentally
/// rewrite a group count like `Q(3)`.
function swapAbbrevColour(abbr: string): string {
  if (abbr.endsWith(".R")) return abbr.slice(0, -2) + ".G";
  if (abbr.endsWith(".G")) return abbr.slice(0, -2) + ".R";
  return abbr;
}
